/**
 * The four bits of markdown the assistant is allowed to use.
 *
 * Paragraphs, `-`/`1.` lists, `**bold**` and `` `code` `` — no headings, no
 * tables, no fences, no links. That is not a limitation to work around later: a
 * chat drawer 420 px wide has no room for a table, and headings inside a message
 * bubble read as noise. The system prompt states the same four, so what the
 * model writes and what this renders are one decision made once.
 *
 * Hand-written rather than a dependency because the whole grammar is two regexes
 * and a loop, and because a markdown library is an HTML sink — everything here
 * goes through React's escaping and there is no `dangerouslySetInnerHTML`
 * anywhere, so a model that emits `<script>` renders the characters.
 */

import { Fragment, type ReactNode } from 'react';
import { userContent } from '@/lib/posthog';
import { cn } from '@/lib/utils';

/** `**bold**` and `` `code` ``, in one pass so neither can nest inside the other. */
const INLINE = /(\*\*[^*]+\*\*|`[^`]+`)/g;

/** Renders one span of inline markup. */
function inline(text: string, keyPrefix: string): ReactNode[] {
  return text.split(INLINE).map((piece, index) => {
    const key = `${keyPrefix}-${index}`;
    if (piece.startsWith('**') && piece.endsWith('**') && piece.length > 4) {
      return (
        <strong key={key} className="font-semibold">
          {piece.slice(2, -2)}
        </strong>
      );
    }
    if (piece.startsWith('`') && piece.endsWith('`') && piece.length > 2) {
      return (
        <code
          key={key}
          className="rounded bg-muted px-1 py-0.5 font-mono text-[0.85em] text-foreground"
        >
          {piece.slice(1, -1)}
        </code>
      );
    }
    return <Fragment key={key}>{piece}</Fragment>;
  });
}

type Block =
  | { readonly kind: 'p'; readonly lines: readonly string[] }
  | { readonly kind: 'ul' | 'ol'; readonly items: readonly string[] };

const BULLET = /^\s*[-*]\s+(.*)$/;
const NUMBER = /^\s*\d+[.)]\s+(.*)$/;

/** Group lines into blocks. A blank line, or a change of shape, ends one. */
function parse(source: string): readonly Block[] {
  const blocks: Block[] = [];
  let paragraph: string[] = [];
  let list: { kind: 'ul' | 'ol'; items: string[] } | null = null;

  const flush = () => {
    if (paragraph.length > 0) {
      blocks.push({ kind: 'p', lines: paragraph });
      paragraph = [];
    }
    if (list !== null) {
      blocks.push({ kind: list.kind, items: list.items });
      list = null;
    }
  };

  for (const line of source.split('\n')) {
    const bullet = BULLET.exec(line);
    const numbered = bullet === null ? NUMBER.exec(line) : null;
    const kind = bullet !== null ? 'ul' : numbered !== null ? 'ol' : null;

    if (kind !== null) {
      if (paragraph.length > 0) {
        blocks.push({ kind: 'p', lines: paragraph });
        paragraph = [];
      }
      if (list === null || list.kind !== kind) {
        if (list !== null) blocks.push({ kind: list.kind, items: list.items });
        list = { kind, items: [] };
      }
      list.items.push((bullet?.[1] ?? numbered?.[1] ?? '').trim());
      continue;
    }

    if (line.trim() === '') {
      flush();
      continue;
    }
    if (list !== null) {
      blocks.push({ kind: list.kind, items: list.items });
      list = null;
    }
    paragraph.push(line);
  }
  flush();
  return blocks;
}

export interface MarkdownProps {
  readonly text: string;
  readonly className?: string;
}

/** Render the assistant's light markdown. */
export function Markdown({ text, className }: MarkdownProps) {
  const blocks = parse(text);
  return (
    // Every use of this component is the assistant talking about the brewer's
    // own recipe, so the whole block is theirs rather than ours. See
    // lib/posthog's USER_CONTENT_ATTR.
    <div {...userContent} className={cn('flex flex-col gap-2 text-sm leading-relaxed', className)}>
      {blocks.map((block, index) => {
        const key = `block-${index}`;
        if (block.kind === 'p') {
          return (
            <p key={key} className="whitespace-pre-wrap">
              {/* Markdown blocks have no identity beyond their position — there
                  is nothing stabler to key on, and they are never reordered. */}
              {block.lines.map((line, lineIndex) => (
                // biome-ignore lint/suspicious/noArrayIndexKey: position is the identity
                <Fragment key={`${key}-${lineIndex}`}>
                  {lineIndex > 0 && '\n'}
                  {inline(line, `${key}-${lineIndex}`)}
                </Fragment>
              ))}
            </p>
          );
        }
        const List = block.kind === 'ul' ? 'ul' : 'ol';
        return (
          <List
            key={key}
            className={cn(
              'flex flex-col gap-1 pl-5',
              block.kind === 'ul' ? 'list-disc' : 'list-decimal',
            )}
          >
            {block.items.map((item, itemIndex) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: position is the identity
              <li key={`${key}-${itemIndex}`} className="marker:text-muted-foreground">
                {inline(item, `${key}-${itemIndex}`)}
              </li>
            ))}
          </List>
        );
      })}
    </div>
  );
}
